import { Button } from 'components/universal/button';
import { useTranslation } from 'react-i18next';
import { Course } from 'types/elearning';
import { employeesInterface } from 'types/employee';
import cls from './downloadCertificateModal.module.scss';
import { Trans } from 'react-i18next';
import CompleteIcon from 'assets/images/icon/complete-icon.svg';
import InCompleteIcon from 'assets/images/icon/incomplete-icon.svg';

interface DownloadCertificateModalProps {
    downloadCertificateModalType: 'bulk' | 'single' | null;
    selectedCourseIds: number[];
    allCourses: Course[];
    coursesLoading: boolean;
    coursesError: any;
    downloadModalEmployee?: employeesInterface | null;
    setSelectedCourseIds: (ids: number[]) => void;
    confirmDownloadCertificateModal: () => void;
    cancelDownloadCertificateModal: () => void;
}

export const DownloadCertificateModal = ({
    downloadCertificateModalType,
    selectedCourseIds,
    allCourses,
    coursesLoading,
    coursesError,
    downloadModalEmployee,
    setSelectedCourseIds,
    confirmDownloadCertificateModal,
    cancelDownloadCertificateModal,
}: DownloadCertificateModalProps) => {
    const { t } = useTranslation();

    if (!downloadCertificateModalType) {
        return null;
    }
    const isSingle = downloadCertificateModalType === 'single';
    const employeeName = downloadModalEmployee 
        ? `${downloadModalEmployee.first_name} ${downloadModalEmployee.last_name}` 
        : '';

    const completedCourseIds = new Set(
        (downloadModalEmployee?.courses ?? [])
            .filter(a => a.is_done === true)
            .map(a => a.id)
    );
    
    return (
        <>
            <h3 className={cls.modalTitle}>
                {isSingle
                    ? `${t('employeeManagement.downloadCertificateDialogTitle')}: ${employeeName}`
                    : t('employeeManagement.downloadCertificatesDialogTitle')}
            </h3>

            { !isSingle && (
                <p className={cls.modalDescription}>
                    <Trans i18nKey="employeeManagement.downloadCertificatesDialogDescription" components={{ bold: <strong /> }} /> 
                </p>
            )}
            
            {coursesLoading && <p>{t('employeeManagement.loadingCourses')}</p>}
            {coursesError && <p>{t('employeeManagement.coursesLoadError')}</p>}

            {allCourses.length > 0 && (
                <div className={`${cls.courseCheckboxes} ${isSingle ? cls.courseCheckboxesSingle : ''}`}>
                    {allCourses
                        .map((course) => (
                            <label key={course.id} className={`${cls.checkboxItem} ${isSingle ? (completedCourseIds.has(course.id) ? cls.completedCourse : cls.incompleteCourse) : ''}`}>
                                <input
                                    type={'checkbox'}
                                    name={`course-${course.id}`}
                                    className={cls.checkbox}
                                    value={course.id}
                                    checked={selectedCourseIds.includes(course.id)}
                                    disabled={isSingle && !completedCourseIds.has(course.id)}
                                    onChange={(e) => {
                                        const newIds = e.target.checked
                                            ? [...selectedCourseIds, course.id]
                                            : selectedCourseIds.filter((id) => id !== course.id);
                                        setSelectedCourseIds(newIds);
                                    }}
                                />
                                <span className={cls.fakeCheckbox}></span>
                                <span className={cls.courseCheckboxesLabel}>{course.name || course.path}</span>
                                {isSingle && (
                                    <img
                                        className={cls.courseStatusIcon}
                                        src={completedCourseIds.has(course.id) ? CompleteIcon : InCompleteIcon}
                                        alt={completedCourseIds.has(course.id) ? 'complete' : 'incomplete'}
                                    />
                                )}
                            </label>
                        ))}
                </div>
            )}

            { isSingle && (
                <p className={`${cls.modalDescription} ${cls.modalDescriptionBottom}`}>
                    {t('employeeManagement.downloadCertificateDialogDescription')}
                </p>
            )}

            <div className={cls.downloadModalActions}>
                <Button className={cls.button} styleType="border" onClick={cancelDownloadCertificateModal}>
                    {t('employeeManagement.cancel')}
                </Button>
                <Button
                    className={cls.button}
                    onClick={confirmDownloadCertificateModal}
                    disabled={selectedCourseIds.length === 0}
                >
                    {t('employeeManagement.generateAndDownload')}
                </Button>
            </div>
        </>
    );
};
